import type { AxiosRequestConfig } from "axios";
import { reject } from "lodash";
import { defineStore } from "pinia";
import type { Ref, UnwrapNestedRefs } from "vue";
import { reactive, ref } from "vue";
import { useLanguageStore } from "../../backend/stores/languageStore.ts";
import type { ProductResponse } from "../../responses/ProductResponse";
import { api } from "../scripts/api";

export const useProductStore = defineStore("product", () => {
    const languageStore = useLanguageStore();

    const products: Ref<ProductResponse[]> = ref([]);
    const categorySlug: Ref<string> = ref("");
    const toggleGroupModules: Ref<boolean> = ref(false);
    const toggleRecurringModules: Ref<boolean> = ref(false);
    const toggleAll: Ref<boolean> = ref(false);
    const productCount: Ref<number> = ref(0);

    const getProductRequest: UnwrapNestedRefs<{
        timeout: number | null;
        abort: AbortController | null;
    }> = reactive({
        timeout: null,
        abort: null,
    });
    const getPropertiesRequest: UnwrapNestedRefs<{
        timeout: number | null;
        abort: AbortController | null;
    }> = reactive({
        timeout: null,
        abort: null,
    });

    const getProducts = async () => {
        return new Promise((resolve, reject) => {
            if (getProductRequest.abort !== null) {
                getProductRequest.abort.abort();
            }

            getProductRequest.abort = new AbortController();

            try {
                const category = categorySlug.value
                    ? `/${categorySlug.value}`
                    : "";
                const filters = {
                    show_group_courses: toggleGroupModules.value,
                    show_recurring_courses: toggleRecurringModules.value,
                };

                api()
                    .post<ProductResponse>(
                        `/site/products${category}/`,
                        {
                            lang: languageStore.currentIso(),
                            filters: filters,
                            all: toggleAll.value,
                        },
                        {
                            signal: getProductRequest.abort.signal,
                        } as AxiosRequestConfig,
                    )
                    .then((response) => {
                        setProducts(response.data.data);
                        productCount.value = response.data.total_count;
                        resolve(response.data);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const getProductProperties = (product: ProductResponse) => {
        if (getPropertiesRequest.abort !== null) {
            getPropertiesRequest.abort.abort();
        }
        getPropertiesRequest.abort = new AbortController();

        try {
            return api()
                .post(
                    `/site/product/properties/`,
                    {
                        lang: languageStore.currentIso(),
                        product_id: product.id,
                    },
                    {
                        signal: getPropertiesRequest.abort.signal,
                    } as AxiosRequestConfig,
                )
                .then((response) => {
                    return response.data;
                });
        } catch (e) {
            reject(e);
            return [];
        }
    };

    const setProducts = (newProducts: ProductResponse[]) => {
        products.value = newProducts;
    };

    const setCategorySlug = (slug: string) => {
        categorySlug.value = slug;
    };

    return {
        products,
        categorySlug,
        toggleGroupModules,
        toggleRecurringModules,
        toggleAll,
        productCount,
        getProducts,
        getProductProperties,
        setProducts,
        setCategorySlug,
    };
});
